import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import { useCurrentUser } from "@/shared/auth/useAuth";
import { Role } from "@/shared/types/role";
import { CatalogTabs } from "./CatalogTabs";
import { CATALOG_TABS, panelId, tabId, type CatalogTab } from "./tab-ids";
import { ProductsSection } from "./ProductsSection";
import { PricesSection } from "./PricesSection";
import { TaxRatesSection } from "./TaxRatesSection";
import { RefreshCatalogButton } from "./RefreshCatalogButton";

export function CatalogPage() {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const canEdit = currentUser?.role === Role.WANDERFIT_ADMIN;

  const [tab, setTab] = useState<CatalogTab>(CATALOG_TABS[0]);

  return (
    <div data-testid="catalog-screen" className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <p className="brand-eyebrow">{t("catalog.eyebrow")}</p>
          <h1 className="text-4xl">{t("catalog.title")}</h1>
          <p className="font-sans text-base font-normal text-muted-foreground">
            {t("catalog.subtitle")}
          </p>
        </div>
        {canEdit && <RefreshCatalogButton />}
      </header>

      {!canEdit && (
        <div
          data-testid="catalog-readonly-banner"
          className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2"
        >
          <Info className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <p className="font-sans text-sm font-normal text-muted-foreground">
            {t("catalog.readOnlyBanner")}
          </p>
        </div>
      )}

      <CatalogTabs value={tab} onChange={setTab} />

      {/*
       * One panel per tab so every tab's `aria-controls` resolves to a real
       * node, even when its content isn't on screen. We still mount only the
       * active section's tree to keep the existing fetch profile (one list
       * query per visited tab; queries cache for 60s).
       */}
      {CATALOG_TABS.map((current) => (
        <div
          key={current}
          id={panelId(current)}
          role="tabpanel"
          aria-labelledby={tabId(current)}
          hidden={current !== tab}
        >
          {current === tab && current === "products" && <ProductsSection canEdit={canEdit} />}
          {current === tab && current === "prices" && <PricesSection canEdit={canEdit} />}
          {current === tab && current === "tax-rates" && <TaxRatesSection canEdit={canEdit} />}
        </div>
      ))}
    </div>
  );
}
